import React, { useContext, useEffect, useState } from "react";
import {
  Col,
  Form,
  Row
} from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ESInput from "../../controls/ESInput";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import {
  getLocationById,
  insertLocation,
  updateLocation,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  checkIfSuperAdmin,
  getCachedData,
  Option,
  queryClient
} from "../../utils/common";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import Loader from "../loader/Loader";
import "./LocationList.scss";

interface Location {
  LocationId?: number;
  LocationName: string;
  DepartmentId: number;
  IsActive: boolean;
  CreateUserId?: number;
  UpdateUserId?: number | null;
  CreateDate?: string;
  UpdateDate?: string | null;
}

interface AddLocationProps {
  locationId?: number | null;
  onClose: () => void;
}

const AddLocation: React.FC<AddLocationProps> = ({ locationId, onClose }) => {
  const methods = useForm<Location>({
    defaultValues: {
      LocationName: "",
      DepartmentId: 0,
      IsActive: true,
    },
  });
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<number>(0);
  const { appContextdata } = useContext<any>(UserContext);

  const fetchLocationDetailsById = async (
    locationId: number,
    superadmin: boolean
  ) => {
    if (locationId) {
      {
        if ((superadmin && organizations?.length >= 1) || superadmin === false)
          setLoading(true);
        axiosPost(getLocationById, {
          LocationId: locationId,
        })
          .then((response: any) => {
            if (response?.data?.Status === 200) {
              const data = response.data.ExtraData;
              const transformedData = {
                ...data,
                OrganizationId: organizations?.find(
                  (org: Option) => org.value === data.OrganizationId
                )
                  ? {
                      label: organizations?.find(
                        (org: Option) => org.value === data.OrganizationId
                      )?.label,
                      value: data.OrganizationId,
                    }
                  : null,
              };
              methods.reset(transformedData);
              setSelectedOrganization(data.OrganizationId);
            } else {
              showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
            }
          })
          .catch((error) => {
            showToast("Error fetching location details", ToastTypeCode?.ERROR);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        if (locationId && organizations?.length >= 1) {
          fetchLocationDetailsById(locationId, true);
        }
      } else {
        if (locationId) {
          fetchLocationDetailsById(locationId, false);
        }
      }
    }
  }, [appContextdata?.Role, locationId, organizations, methods]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData('organizations')) as Option[];
        setOrganizations(options);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const handleOrganizationChange = async (selectedOption: any) => {
    const organizationId = selectedOption?.value;
    setSelectedOrganization(organizationId);
  };

  const onSubmit: SubmitHandler<Location> = async (data) => {
    const transformedData = {
      ...data,
      IsActive: data.IsActive ? 1 : 0,
      OrganizationId: selectedOrganization
        ? selectedOrganization
        : appContextdata?.Organization,
      CreateDate: new Date().toISOString()
    };
    setLoading(true);
    try {
      const response: any = locationId
        ? await axiosPost(updateLocation, transformedData)
        : await axiosPost(insertLocation, transformedData);

      if (response?.data?.Status === 200) {
         queryClient.invalidateQueries("location");
        const message = locationId
          ? "Location updated successfully"
          : "Location created successfully";
        showToast(message, ToastTypeCode?.SUCCESS);
        onClose();
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error saving location", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="sub-card">
        <div>
          {loading ? (
            <Loader />
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Row>
                  {isSuperAdmin && (
                    <Form.Group as={Col} md="3">
                      <Form.Label>
                        Organization <span className="text-danger">*</span>
                      </Form.Label>
                      <ESSearchDropdown
                        name="OrganizationId"
                        placeholder="Organization"
                        options={organizations}
                        onChange={handleOrganizationChange}
                      />
                    </Form.Group>
                  )}
                  <Form.Group as={Col} md="3">
                    <Form.Label>
                      Location name
                      <span className="text-danger">*</span>
                    </Form.Label>

                    <ESInput
                      className="form-control"
                      name="LocationName"
                      placeholder="Location name"
                      type="text"
                      required
                      minLength={2}
                      maxLength={200}
                    />
                  </Form.Group>

                  <Col md="2">
                    <div className="form-check">
                      <ESInput
                        name="IsActive"
                        type="checkbox"
                        className="form-check-input"
                        placeholder={""}
                      />
                      <label htmlFor="IsActive" className="form-check-label">Is Active</label>
                    </div>
                  </Col>
                  <Col md="3">
                    <button type="submit" className="save-btn">
                    {locationId ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="back-btn"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddLocation;
